import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Container, Typography, Button, TextField, Box, Paper, CircularProgress, List, ListItem, ListItemButton, ListItemText, Divider } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, Outlet, useLocation, Link as RouterLink } from 'react-router-dom';

// Seus componentes importados
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import CorridaPage from './components/CorridaPage';
import VisaoGeralPage from './components/VisaoGeralPage'; // Importar VisaoGeralPage

// Seu tema
const theme = createTheme({
  palette: {
    primary: { main: '#1a237e' },
    secondary: { main: '#00C49F' },
    background: { default: '#f5f5f5' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiCard: { styleOverrides: { root: { borderRadius: 8, transition: 'transform 0.3s, box-shadow 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 8px 20px rgba(0,0,0,0.12)'}}}},
    MuiPaper: { styleOverrides: { root: { borderRadius: 8 }}},
  }
});

// --- Componente da Página de Login ---
const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || 'Erro ao tentar fazer login.');
            }
            localStorage.setItem('authToken', data.token); 
            localStorage.setItem('user', JSON.stringify(data.user)); 
            navigate('/'); 
            window.location.reload(); 
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Paper elevation={6} sx={{ marginTop: 8, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2 }}>
                <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
                    Login do Dashboard
                </Typography>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                    <TextField margin="normal" required fullWidth id="username" label="Usuário" name="username" autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} sx={{ mb: 2 }} />
                    <TextField margin="normal" required fullWidth name="password" label="Senha" type="password" id="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mb: 2 }} />
                    {error && <Typography color="error" align="center" sx={{ mt: 1, mb:1 }}>{error}</Typography>}
                    <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2, padding: '10px 0' }}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

// --- Componente para Proteger Rotas ---
const ProtectedRoute: React.FC = () => {
    const token = localStorage.getItem('authToken');
    return token ? <Outlet /> : <Navigate to="/login" replace />;
};

// --- Componente UnitSelector ---
const UnitSelector: React.FC = () => {
  const [unidades, setUnidades] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUnidades = async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const response = await fetch('/api/unidades', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
             localStorage.removeItem('authToken');
             localStorage.removeItem('user');
             navigate('/login');
          }
          throw new Error(`Erro ao buscar unidades: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setUnidades(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUnidades();
  }, [navigate]);
  
  if (loading) return <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', height: '50vh'}}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{textAlign:'center', mt:4}}>Erro ao carregar unidades: {error}</Typography>;

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom textAlign="center">
          Análise de Desempenho
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" component="h2" gutterBottom>
            Visão Consolidada
        </Typography>
        <Button variant="contained" component={RouterLink} to="/corrida-vendedores" fullWidth sx={{ py: 1.5, mb: 2 }}>
            Corrida de Vendedores
        </Button>
        <Button variant="contained" component={RouterLink} to="/visao-geral" fullWidth sx={{ py: 1.5, mb: 2 }}>
            Visão Geral do Grupo
        </Button>
        <Divider sx={{ my: 2 }}>OU</Divider>
        <Typography variant="h6" component="h2" gutterBottom>
          Análise por Unidade de Negócio
        </Typography>
        {unidades.length === 0 && !loading && <Typography textAlign="center">Nenhuma unidade disponível.</Typography>}
        <List>
          {unidades.map((unidade) => (
            <ListItem disablePadding key={unidade}>
              <ListItemButton component={RouterLink} to={`/dashboard?unidade=${encodeURIComponent(unidade)}`}>
                <ListItemText primary={unidade} primaryTypographyProps={{variant: 'h6'}} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Container>
  );
};


// --- Wrapper para a Página do Dashboard ---
const DashboardPageWrapper: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const unidade = queryParams.get('unidade');
  const userString = localStorage.getItem('user');
  
  if (!userString) {
    return <Navigate to="/login" replace />;
  }
  const user = JSON.parse(userString);

  if (!unidade) {
    return (
      <Container maxWidth="xl" sx={{textAlign: 'center', mt: 4, p:3}}>
        <Typography variant="h5" color="error">Unidade de negócio não especificada.</Typography>
        <Button variant="contained" sx={{mt: 2}} component={RouterLink} to="/">Voltar</Button>
      </Container>
    );
  }
  return <Dashboard unidadeSelecionada={unidade} user={user} />;
};


// --- Componente Principal da Aplicação ---
function App() {
  const token = localStorage.getItem('authToken');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {token && <Header />} 
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
                <Route path="/" element={<UnitSelector />} />
                <Route path="/dashboard" element={<DashboardPageWrapper />} />
                <Route path="/corrida-vendedores" element={<CorridaPage />} />
                <Route path="/visao-geral" element={<VisaoGeralPage />} /> {/* Nova rota para VisaoGeralPage */}
            </Route>
            <Route path="*" element={<Navigate to={token ? "/" : "/login"} replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;

